import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import generateAxisChart from './generateAxiesChart';
import generateCircleCharts from './generateCircleChart';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

import { INote } from '../../../../../common/services/http/analysisContentService';
import { ICurrency } from '../../../types/ICurrency';
import { ICashFlowCircle } from '../../../types/ICashFlowCircle';
import { ICashFlowAxis } from '../../../types/ICashFlowAxis';
import { IDrillDownNode } from '../../../types/IDrillDownNode';
import { AnalysisContentWidgetDataModel } from '../../../../../models/AnalysisContentWidgetDataModel';

const useGetChartData = (setup, periods, measures, widget, notes, scenarios, selectedObject, selectedPeriod, boardSelectedCurrency, investorObjectId) => {
  const [gridData, setGridData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [series, setSeries] = useState<any[]>([]);
  const [chartType, setChartType] = useState<string>('');
  const [categories, setCategories] = useState<any[]>([]);
  const [valueAxis, setValueAxis] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<ICurrency[]>([]);
  const [selectedCurrency, setSelectedCurrency] = useState<ICurrency[]>([]);
  const [cashFlow, setCashFlow] = useState<ICashFlowCircle | ICashFlowAxis | null>(null);
  const [drillDownData, setDrillDownData] = useState<IDrillDownNode[] | null>(null);
  const [popupVisible, setPopUpVisible] = useState<boolean>(false);
  const [breadCrumbs, setBreadcrumbs] = useState<any[]>([{ id: 0, name: '', level: 0, node: null }]);
  const [gridOptions, setGridOptions] = useState<any[]>([]);
  const [chartNotes, setChartNotes] = useState<INote[]>([]);
  const [chartContentEnriched, setChartContentEnriched] = useState<any[]>([]);

  let tempChartNotesArray: INote[] = [];
  let periodIdField = 'id';
  let focusedNode;
  let compareNode;

  const investor = useSelector((state: RootState) => state.investor.investor);

  const { investorId } = useParams();

  const getChartMeasures = (setup: any, measures: any[]) => {
    const measuresArray = [];

    setup.series.forEach((x) => {
      let tempMeasure = measures.find((element) => element.id === x.measureId);
      if (tempMeasure) measuresArray.push(tempMeasure);
    });

    return measuresArray;
  };

  let chartsMeasures = getChartMeasures(setup, measures);

  const widgetDataRef = useRef<AnalysisContentWidgetDataModel>();

  const _getData = (widgetInstance) => {
    return boardSelectedCurrency && widgetInstance.fetchNodeData(selectedPeriod, boardSelectedCurrency.currency).then((data) => {
      return data;
    });
  };

  let cashflowDialog;

  const drillDownInfo = (nodeId) => {
    let drillDownInfo = [];

    if (widget.setup.enableDrillDown) {
      let node = selectedObject.findNode(nodeId);

      if (node.childNodes.length > 0) {
        let hasNonGroups = false;
        node.childNodes.forEach(function(child) {
          if (child.isGroup) drillDownInfo.push({ id: child.id, name: child.name });
          else hasNonGroups = true;
        });

        if (hasNonGroups) drillDownInfo.push({ id: nodeId, name: null });
      }
    }
    return drillDownInfo;
  };

  const handleSeriesClickEventCircle = (event, widgetInstance) => {
    if (!event.dataItem || !event.dataItem.lookupRef) return;

    let ref = event.dataItem.lookupRef;
    let period = selectedPeriod;
    let value = chartData.find(function(item) {
      return item.nodeId === parseInt(ref.nodeId) && item.measureId === parseInt(ref.measureId) && item.periodId === period[periodIdField];
    });

    let measure = measures.find(function(item) {
      return item.id === ref.measureId;
    });
    let node = widgetInstance.nodes.find(function(item) {
      return item.id === ref.nodeId;
    });

    if (value) {
      setDrillDownData(drillDownInfo(node.id));

      return {
        nodeId: node.id,
        scenarioId: node.scenarioId,
        valueId: value.id,
        value: event.dataItem.amount,
        percentage: event.dataItem.percentage,
        measureId: measure.id,
        viewCashflows: setup.series[0].viewCashflows,
        currency: node.currency,
        period: period,
        valueInfo: {
          name: node.name,
          measure: measure,
          amount: event.dataItem.amount,
          scale: setup.series[0].scale,
        },
      };
    }
  };

  const _seriesData = (event) => {
    let ref = event.series.data[0].lookupRef;

    if (!ref) return;

    let info = {
      nodeId: null,
      measureId: null,
      period: null,
      amount: null,
      value: null,
    };

    if (setup.singlePeriod) {
      info.nodeId = event.dataItem.nodeId;
      info.measureId = parseInt(ref.measureId);
      info.amount = event.dataItem.value * (ref.amountsIn || 1);
      (info.value = event.dataItem.value), (info.period = selectedPeriod);
    } else {
      info.nodeId = parseInt(ref.nodeId);
      info.measureId = parseInt(ref.measureId);
      info.amount = event.dataItem.amount * ref.amountsIn;

      (info.value = event.dataItem),
        (info.period = periods.find(function(item) {
          return item.name === event.category;
        }));
    }

    return info;
  };

  const filterNotesForChart = (notes: INote[]): void => {
    let currentColumns = setup.series;
    if (notes) {
      for (let i = 0; i < notes.length; i++) {
        for (let j = 0; j < notes[i].measures.length; j++) {
          let tempNoteMeasureId = notes[i].measures[j].measureId;
          currentColumns.map((el) => {
            if (el.measureId === tempNoteMeasureId && !tempChartNotesArray.includes(notes[i])) {
              tempChartNotesArray.push(notes[i]);
            }
          });
        }
      }
    }

    setChartNotes(tempChartNotesArray);
    extractNotesForChartItems();
  };

  const extractNotesForChartItems = (): any => {
    let currentColumns = setup.series;
    let copiedChartContent = currentColumns.map((object) => ({ ...object }));
    for (let i = 0; i < currentColumns.length; i++) {
      let tempItemNotes: INote[] = [];
      for (let j = 0; j < notes.length; j++) {
        let tempNoteMeasures = notes[j].measures.map((el) => el.measureId);
        if (tempNoteMeasures.includes(currentColumns[i].measureId)) {
          tempItemNotes.push(notes[j]);
          copiedChartContent[i].hasNotes = true;
          copiedChartContent[i].notes = tempItemNotes;
          setChartContentEnriched(copiedChartContent);
        }
      }
    }
  };

  const handleSeriesClickEventAxes = (event, widgetInstance) => {
    let ref = event.series.data[0].lookupRef;
    if (!ref) return;

    let info = _seriesData(event);

    if (info.nodeId && info.measureId && info.period) {
      let value = chartData.find(function(item) {
        return item.nodeId === info.nodeId && item.measureId === info.measureId && item.periodId === info.period[periodIdField];
      });
      let measure = measures.find(function(item) {
        return item.id === info.measureId;
      });
      let node = widgetInstance.nodes.find(function(item) {
        return item.id === info.nodeId;
      });

      if (value) {
        setDrillDownData(drillDownInfo(node.id));
        return {
          nodeId: node.id,
          scenarioId: node.scenarioId,
          valueId: value.id,
          value: info.value,
          measureId: info.measureId,
          viewCashflows: ref.viewCashflows,
          currency: node.currency,
          period: info.period,
          valueInfo: {
            name: node.name,
            measure: measure,
            amount: info.amount,
            scale: ref.scale,
          },
        };
      }
    }
    return undefined;
  };

  const seriesClick = (event) => {
    switch (setup.type) {
      case 'line':
      case 'bar':
      case 'column':
      case 'area':
        setCashFlow(handleSeriesClickEventAxes(event, widgetDataRef.current));
        break;
      case 'pie':
      case 'donut':
        setCashFlow(handleSeriesClickEventCircle(event, widgetDataRef.current));
        break;
    }
  };

  const drillDown = (nodeId, widgetInstance) => {
    if (popupVisible) {
      setPopUpVisible(false);
    }
    let node = selectedObject.findNode(nodeId);

    if (node) {
      focusedNode = node;

      widgetInstance.defineNodesForParent(selectedObject, node.id);

      if (widget.setup.showParentOnDrillDown) compareNode = focusedNode.isGroup ? focusedNode.parentNode : focusedNode;

      if (compareNode) widgetInstance.nodes.push(compareNode);

      _getData(widgetInstance).then((res) => {
        let appendParent = false;
        if (node.parentNode && node.parentNode.parentNode) {
          appendParent = !breadCrumbs.find(function(item) {
            return item.id === node.parentNode.id;
          });
        }
        setBreadcrumbs([
          ...breadCrumbs,
          {
            id: node.id,
            name: (appendParent ? node.parentNode.name + ' / ' : '') + node.name,
            level: breadCrumbs.length,
            node: node,
          },
        ]);

        setChartData(res);
      });
    }
  };

  const drillToLevel = (node, widgetInstance) => {
    let level;

    if (node.level === 0) {
      focusedNode = null;
      compareNode = null;

      if (widget.setup.sliceGroup && level) widgetInstance.defineNodesForCategoryTypeLevel(selectedObject, widget.setup.sliceGroup, level);
      else widgetInstance.defineNodes(selectedObject);

      _getData(widgetInstance).then(function(res) {
        let newBrCrumbsArr = breadCrumbs;
        newBrCrumbsArr.splice(1);
        setBreadcrumbs(newBrCrumbsArr);
        setChartData(res);
      });
      return;
    }

    focusedNode = node.node;
    widgetInstance.defineNodesForParent(selectedObject, node.node.id, true);
    if (widget.setup.showParentOnDrillDown) compareNode = focusedNode.isGroup ? focusedNode.parentNode : focusedNode;
    if (compareNode) widgetInstance.nodes.push(compareNode);
    let newBrCrumbsArr = breadCrumbs;
    newBrCrumbsArr.splice(node.level + 1);
    setBreadcrumbs(newBrCrumbsArr);
    _getData(widgetInstance).then(function(res) {
      setChartData(res);
    });
  };

  const _onExcelExport = () => {
    console.log('excel export');
  };

  const _gridOptions = (periodTitle) => {
    // grid options

    let columns = [
      {
        field: 'periodName',
        title: periodTitle,
        width: 180,
        locked: true,
        sortable: true,
      },
    ];

    let series = widget.setup.series;
    let nodes = widgetDataRef.current.nodes;

    for (let i = 0; i < series.length; i++) {
      let ser = series[i];
      let measure = measures.find((item) => {
        return item.id.toString() === ser.measureId.toString();
      });
      if (!measure) continue;

      let nodeColumns: any[] = columns;

      if (series.length > 1) {
        let column: any = {
          title: ser.name,
          headerAttributes: {
            style: 'text-align: center',
          },
          columns: [],
        };
        columns.push(column);
        nodeColumns = column.columns;
      }

      if (nodes) {
        for (let j = 0; j < nodes.length; j++) {
          let node = nodes[j];
          let field = '_' + node.id + '_' + ser.measureId;

          nodeColumns.push({
            field: field,
            title: node.name,
            width: 200,
            headerAttributes: {
              style: 'text-align: right',
            },
            attributes: {
              style: 'text-align: right',
            },
            node: node,
            measure: measure,
          });
        }
      }
    }

    _clearEmptyGridColumns(columns);

    return {
      dataSource: {
        data: gridData,
        sort: { field: 'endDate', dir: 'asc' },
      },
      columns: columns,
      excel: {
        fileName: widget.name + '.xlsx',
      },
      excelExport: _onExcelExport,
    };
  };

  function _columnHasValues(column) {
    for (let j = 0; j < gridData.length; j++) {
      let row = gridData[j];
      if (row[column.field] !== undefined && row[column.field] !== null) {
        return true;
      }
    }
    return false;
  }

  function _clearEmptyGridColumns(columns) {
    for (let i = columns.length - 1; i > 0; i--) {
      let column = columns[i];
      if (column.columns && column.columns.length > 0) {
        for (let j = column.columns.length - 1; j > -1; j--) {
          let subCol = column.columns[j];
          if (!_columnHasValues(subCol)) column.columns.splice(j, 1);
        }
      } else {
        if (!_columnHasValues(column)) columns.splice(i, 1);
      }
    }
  }

  const gridCellClick = (dataItem, field, measureId, nodeId) => {
    let measure = measures.find(function(item) {
      return item.id.toString() === measureId.toString();
    });

    let node = widgetDataRef.current.nodes.find(function(item) {
      return item.id === nodeId;
    });

    let value = chartData.find(function(item) {
      return item.nodeId === nodeId && item.measureId === measureId && item.periodId === dataItem.periodId;
    });

    let period = widget.setup.singlePeriod
      ? selectedPeriod
      : periods.find(function(item) {
        return item.id === dataItem.periodId;
      });

    cashflowDialog = {
      nodeId: node.id,
      scenarioId: node.scenarioId,
      valueId: value.id,
      value: dataItem,
      measureId: measureId,
      viewCashflows: true,
      period: period,
      valueInfo: {
        name: node.name,
        measure: measure,
        amount: dataItem[field],
      },
    };

    setDrillDownData(drillDownInfo(node.id));
    setCashFlow(cashflowDialog);
  };

  const _initChart = (chartData) => {
    if (chartData) {
      switch (setup.type) {
        case 'line':
        case 'bar':
        case 'column':
        case 'area':
          //@ts-ignore
          generateAxisChart(setCategories, setValueAxis, setSeries, widgetDataRef.current, setup, measures, chartData, selectedPeriod, periods, setGridData, selectedCurrency.currency === '_');
          setChartType('axes');

          break;
        case 'pie':
        case 'donut':
          generateCircleCharts(widgetDataRef.current, chartData, selectedPeriod, setSeries, setup, setGridData);
          setChartType('circle');
          setLoading(false);
          break;
        default:
          console.log(`setup type unknown ${setup.type}`);
      }
    }
  };

  const handleCurrencyChange = (e) => {
    setSelectedCurrency(e.value);
  };

  const handleFindSelection = (currency, arrayToFind) => {
    if (arrayToFind.find((curr) => curr.currency === currency.currency)) {
      return currency;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const widgetData = new AnalysisContentWidgetDataModel(widget, chartsMeasures, investorId ? parseInt(investorId) : parseInt(investorObjectId), false, undefined);
    widgetData.defineNodes(selectedObject);
    widgetDataRef.current = widgetData;
  }, [investor]);

  useEffect(() => {
    let isMounted = true;
    // @ts-ignore
    setGridOptions(_gridOptions('Time Period', widgetDataRef));
    return () => {
      isMounted = false;
    };
  }, [gridData]);

  useEffect(() => {
    if (notes) filterNotesForChart(notes);
  }, [notes]);

  useEffect(() => {
    //@ts-ignore
    if (widget.setup.type === 'donut' && boardSelectedCurrency && boardSelectedCurrency.currency === '_') {
      return;
    }
    if (boardSelectedCurrency) {
      let currency = handleFindSelection(boardSelectedCurrency, widgetDataRef.current.generateCurrencies(scenarios));
      if (currency) {
        setSelectedCurrency(currency);
      }
    }
  }, [boardSelectedCurrency]);

  useEffect(() => {
    //@ts-ignore
    if (selectedCurrency.currency) {
      widgetDataRef.current.defineNodes(selectedObject);
      setCurrencies(widgetDataRef.current.generateCurrencies(scenarios));
      //@ts-ignore
      widgetDataRef.current.fetchData(selectedPeriod, selectedCurrency.currency).then((data) => {
        setLoading(false);
        setChartData(data);
      });
    }
  }, [selectedObject, selectedPeriod, selectedCurrency]);

  // useEffect cleanup https://stackoverflow.com/questions/53949393/cant-perform-a-react-state-update-on-an-unmounted-component
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      _initChart(chartData);
    }
    return () => {
      isMounted = false;
    };
  }, [chartData]);

  return {
    loading,
    chartData,
    series,
    categories,
    chartType,
    valueAxis,
    seriesClick,
    cashFlow,
    drillDownData,
    drillDown,
    popupVisible,
    setPopUpVisible,
    breadCrumbs,
    widgetData: widgetDataRef.current,
    gridData,
    gridOptions,
    chartNotes,
    chartContentEnriched,
    drillToLevel,
    gridCellClick,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
  };
};

export default useGetChartData;
